import type { PageArgs } from "bunfx";
import { ClientError, navigateTo, RedirectError } from "bunfx";
import { AuthLayout } from "../components/layout";
import { Button } from "../components/ui";
import { usePageTitle } from "../hooks";
import { rpc } from "../rpc";

type LoaderData =
  | { success: true }
  | { success: false; error: string; isExpired: boolean };

export async function load({
  searchParams,
}: {
  searchParams: Record<string, string>;
}): Promise<LoaderData> {
  const userId = searchParams.user;
  const token = searchParams.token;

  if (!userId || !token) {
    return {
      success: false,
      error: "Invalid confirmation link",
      isExpired: false,
    };
  }

  try {
    await rpc.profile.confirmEmailChange({ userId, token });
    // Success - redirect to account page
    throw new RedirectError("/account");
  } catch (err) {
    // Let RedirectError propagate
    if (err instanceof RedirectError) {
      throw err;
    }

    const isExpired =
      err instanceof ClientError && err.code === "invalid_token";

    return {
      success: false,
      error: err instanceof ClientError ? err.message : "Something went wrong",
      isExpired,
    };
  }
}

export function Page({ state }: PageArgs<typeof load>) {
  usePageTitle("Confirm Email");
  const data = state.value;

  // Should only show error state (success redirects)
  if (data.success) {
    return null;
  }

  const { error, isExpired } = data;

  return (
    <AuthLayout
      title={isExpired ? "Link expired" : "Email change failed"}
      subtitle={
        isExpired
          ? "Your email confirmation link has expired. Please request a new one from your account settings."
          : error
      }
      hideLogo
    >
      <div class="flex flex-col gap-3">
        <Button class="w-full" onClick={() => navigateTo("/account")}>
          Go to Account Settings
        </Button>
      </div>
    </AuthLayout>
  );
}
